import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './CustomAutocomplete.css';

const CustomAutocomplete = ({
  id = 'address-input',
  name = 'address',
  onPlaceSelected,
  onInputChange,
  placeholder = "הזן כתובת בישראל...",
  value = "",
  className = "",
  isTransparent = false,
  autoFocus = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isSelecting, setIsSelecting] = useState(false);
  const lastSelectionTimeRef = useRef(0);
  const userInteracted = useRef(false);
  
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Initialize Google Places services (New API only)
  useEffect(() => {
    const initializeGoogleServices = async () => {
      if (window.google && window.google.maps) {
        try {
          // Load the new Places library (Place class available for future use)
          console.log('🗺️ CustomAutocomplete: Loading new Places library...');
          // eslint-disable-next-line no-unused-vars
          const { Place } = await window.google.maps.importLibrary("places");
          console.log('✅ CustomAutocomplete: New Places library loaded - no legacy services needed!');
          
          // No longer initializing any legacy services - using only new APIs
          // AutocompleteSuggestion.fetchAutocompleteSuggestions() for search
          // Place.fetchFields() for place details
          
        } catch (error) {
          console.error('❌ CustomAutocomplete: Failed to load new Places library:', error);
          // Only fallback to legacy if new API completely fails
          if (window.google.maps && window.google.maps.places) {
            console.log('🔄 CustomAutocomplete: Falling back to ALL legacy APIs as last resort...');
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            const dummyMap = new window.google.maps.Map(document.createElement('div'));
            placesService.current = new window.google.maps.places.PlacesService(dummyMap);
          } else {
            setTimeout(initializeGoogleServices, 500);
          }
        }
      } else {
        console.log('⏳ CustomAutocomplete: Google Maps not ready, retrying...');
        setTimeout(initializeGoogleServices, 500);
      }
    };

    initializeGoogleServices();
  }, []);

  // Debounced search
  useEffect(() => {
    // Clear any existing search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }

    // Don't search if we're in the process of selecting a suggestion
    if (isSelecting) {
      console.log('🚫 Skipping search during selection (flag)');
      return;
    }

    // Don't search if we recently made a selection (within 1 second)
    const timeSinceLastSelection = Date.now() - lastSelectionTimeRef.current;
    if (timeSinceLastSelection < 1000) {
      console.log('🚫 Skipping search - recent selection', timeSinceLastSelection + 'ms ago');
      return;
    }

    // Don't search if the user hasn't typed anything (e.g. on mount with pre-filled value)
    if (!userInteracted.current) {
      console.log('🚫 Skipping search - no user interaction');
      return;
    }

    if (!inputValue.trim() || inputValue.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      const timeSinceLastSelectionDelayed = Date.now() - lastSelectionTimeRef.current;
      if (!isSelecting && timeSinceLastSelectionDelayed >= 1000) { // Double-check both conditions
        console.log('⏰ Executing delayed search for:', inputValue);
        searchPlaces(inputValue);
      } else {
        console.log('🚫 Skipping delayed search - selection too recent:', timeSinceLastSelectionDelayed + 'ms ago');
      }
      searchTimeoutRef.current = null;
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [inputValue, isSelecting]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const headerSearchContainer = inputRef.current.closest('#header-search-container');
      const headerRect = headerSearchContainer?.getBoundingClientRect();
      const searchFormRect = inputRef.current.closest('.header-search-form')?.getBoundingClientRect();
      const dropdownHeight = 300; // max-height of dropdown
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      
      // Check if we're on mobile (small screen)
      const isMobile = viewportWidth <= 768;
      
      // For fixed positioning, we DON'T need scroll offset since getBoundingClientRect is viewport-relative
      let top, left, width;
      
      if (isMobile) {
        // Mobile-specific positioning - simpler and more reliable
        const margin = 16;
        const visualViewport = window.visualViewport;
        const isKeyboardOpen = visualViewport && visualViewport.height < window.innerHeight - 50;
        const viewportOffset = isKeyboardOpen ? (visualViewport.offsetTop || 0) : 0;
        // The mobile search form is stacked, so suggestions belong below the
        // whole form rather than between the input and submit button.
        top = (searchFormRect?.bottom ?? inputRect.bottom) + 8 + viewportOffset;
        
        // Center the dropdown horizontally with margins
        const availableWidth = viewportWidth - (2 * margin);
        const anchorWidth = searchFormRect?.width ?? inputRect.width;
        const anchorLeft = searchFormRect?.left ?? inputRect.left;
        width = Math.min(anchorWidth, availableWidth);
        left = Math.max(margin, anchorLeft - (width - anchorWidth) / 2);
        
        // Ensure it doesn't go off the right edge
        if (left + width > viewportWidth - margin) {
          left = viewportWidth - width - margin;
        }
        
        // Keep dropdown below the input on mobile to avoid overlap
        
      } else {
        // Desktop positioning - for fixed elements, use viewport coordinates directly
        const flipUp = spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight;
        
        top = flipUp ? inputRect.top - dropdownHeight - 8 : inputRect.bottom + 8;
        left = inputRect.left;
        width = inputRect.width;
      }
      
      const visualViewport = window.visualViewport;
      const isKeyboardOpen = isMobile && visualViewport && visualViewport.height < window.innerHeight - 50;
      const viewportOffset = isKeyboardOpen ? (visualViewport.offsetTop || 0) : 0;
      const baseTop = (isMobile ? (searchFormRect?.bottom ?? inputRect.bottom) : inputRect.bottom) + 8 + viewportOffset;
      const desktopFormTop = (searchFormRect?.bottom ?? headerRect?.bottom ?? inputRect.bottom) + 8;
      const minTop = isMobile ? baseTop : Math.max(baseTop, desktopFormTop);
      setDropdownPosition({
        top: Math.max(top, minTop),
        left,
        width
      });
      
      // Debug logging for mobile dropdown positioning
      if (isMobile) {
        console.log('📱 Mobile dropdown positioning:', {
          inputRect: {
            top: inputRect.top,
            bottom: inputRect.bottom,
            left: inputRect.left,
            width: inputRect.width
          },
          calculated: { top, left, width },
          viewport: {
            width: viewportWidth,
            height: window.innerHeight
          },
          spaceBelow,
          spaceAbove
        });
      }
    } else if (!isOpen) {
      // Dropdown is closed, reset position
      setDropdownPosition({ top: 0, left: 0, width: 0 });
    }
  }, [isOpen, suggestions]);

  // Handle scroll and resize events to reposition dropdown
  useEffect(() => {
    const handleScrollResize = () => {
      if (isOpen && inputRef.current) {
        const inputRect = inputRef.current.getBoundingClientRect();
        const headerSearchContainer = inputRef.current.closest('#header-search-container');
        const headerRect = headerSearchContainer?.getBoundingClientRect();
        const searchFormRect = inputRef.current.closest('.header-search-form')?.getBoundingClientRect();
        const dropdownHeight = 300;
        const spaceBelow = window.innerHeight - inputRect.bottom;
        const spaceAbove = inputRect.top;
        
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        
        // Check if we're on mobile (small screen)
        const isMobile = viewportWidth <= 768;
        
        // For fixed positioning, we DON'T need scroll offset since getBoundingClientRect is viewport-relative
        let top, left, width;
        
        if (isMobile) {
          // Mobile-specific positioning - simpler and more reliable
          const margin = 16;
          const visualViewport = window.visualViewport;
          const isKeyboardOpen = visualViewport && visualViewport.height < window.innerHeight - 50;
          const viewportOffset = isKeyboardOpen ? (visualViewport.offsetTop || 0) : 0;
          // The mobile search form is stacked, so suggestions belong below the
          // whole form rather than between the input and submit button.
          top = (searchFormRect?.bottom ?? inputRect.bottom) + 8 + viewportOffset;
          
          // Center the dropdown horizontally with margins
          const availableWidth = viewportWidth - (2 * margin);
          const anchorWidth = searchFormRect?.width ?? inputRect.width;
          const anchorLeft = searchFormRect?.left ?? inputRect.left;
          width = Math.min(anchorWidth, availableWidth);
          left = Math.max(margin, anchorLeft - (width - anchorWidth) / 2);
          
          // Ensure it doesn't go off the right edge
          if (left + width > viewportWidth - margin) {
            left = viewportWidth - width - margin;
          }
          
          // Keep dropdown below the input on mobile to avoid overlap
          
        } else {
          // Desktop positioning - for fixed elements, use viewport coordinates directly
          const flipUp = spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight;
          
          top = flipUp ? inputRect.top - dropdownHeight - 8 : inputRect.bottom + 8;
          left = inputRect.left;
          width = inputRect.width;
        }
        
        const visualViewport = window.visualViewport;
        const isKeyboardOpen = isMobile && visualViewport && visualViewport.height < window.innerHeight - 50;
        const viewportOffset = isKeyboardOpen ? (visualViewport.offsetTop || 0) : 0;
        const baseTop = (isMobile ? (searchFormRect?.bottom ?? inputRect.bottom) : inputRect.bottom) + 8 + viewportOffset;
        const desktopFormTop = (searchFormRect?.bottom ?? headerRect?.bottom ?? inputRect.bottom) + 8;
        const minTop = isMobile ? baseTop : Math.max(baseTop, desktopFormTop);
        setDropdownPosition({
          top: Math.max(top, minTop),
          left,
          width
        });
      }
    };

    let rafId = null;
    const scheduleRecalc = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        handleScrollResize();
      });
    };

    if (isOpen) {
      window.addEventListener('scroll', scheduleRecalc);
      window.addEventListener('resize', scheduleRecalc);
      window.visualViewport?.addEventListener('resize', scheduleRecalc);
      window.visualViewport?.addEventListener('scroll', scheduleRecalc);
    }

    return () => {
      window.removeEventListener('scroll', scheduleRecalc);
      window.removeEventListener('resize', scheduleRecalc);
      window.visualViewport?.removeEventListener('resize', scheduleRecalc);
      window.visualViewport?.removeEventListener('scroll', scheduleRecalc);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    };
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, []);

  const searchPlaces = async (query) => {
    // Use new AutocompleteSuggestion.fetchAutocompleteSuggestions() 
    if (window.google?.maps?.places?.AutocompleteSuggestion) {
      try {
        console.log('🔍 CustomAutocomplete: Searching with new AutocompleteSuggestion API for:', query);
        setLoading(true);
        
        // Use new AutocompleteSuggestion API (following migration guide)
        const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          // Restrict to Israel so every city ranks by text relevance. The old
          // locationBias was a 50km circle centered at (31.5, 34.75) in the
          // south — Jerusalem (~54km east) fell OUTSIDE it, so shared street
          // names (e.g. "שלמה מולכו") ranked Rishon LeZion / Tel Aviv above
          // Jerusalem even when the user typed "ירושלים".
          includedRegionCodes: ['il']
        });

        setLoading(false);
        console.log('📍 CustomAutocomplete: New AutocompleteSuggestion API results:', { suggestionsCount: suggestions?.length || 0 });
        console.log('📍 Debug: First suggestion structure:', suggestions?.[0]); // Debug log
        
        if (suggestions && suggestions.length > 0) {
          const formattedSuggestions = suggestions.map((suggestion, index) => {
            try {
              // Log the structure of each suggestion for debugging
              if (index === 0) {
                console.log('📍 Debug: suggestion structure:', suggestion);
                console.log('📍 Debug: placePrediction:', suggestion.placePrediction);
                if (suggestion.placePrediction?.structuredFormat) {
                  console.log('📍 Debug: structuredFormat:', suggestion.placePrediction.structuredFormat);
                }
              }
              
              return {
                placeId: suggestion.placePrediction?.placeId || `temp-${index}`,
                description: suggestion.placePrediction?.text?.text || suggestion.placePrediction?.description || `Unknown ${index}`,
                mainText: suggestion.placePrediction?.structuredFormat?.mainText?.text || 
                         suggestion.placePrediction?.structuredFormat?.mainText || 
                         suggestion.placePrediction?.text?.text || 
                         `Unknown ${index}`,
                secondaryText: suggestion.placePrediction?.structuredFormat?.secondaryText?.text || 
                              suggestion.placePrediction?.structuredFormat?.secondaryText || 
                              ''
              };
            } catch (error) {
              console.error('❌ Error processing suggestion:', error, suggestion);
              return {
                placeId: `error-${index}`,
                description: `Error suggestion ${index}`,
                mainText: `Error ${index}`,
                secondaryText: ''
              };
            }
          });
          
          setSuggestions(formattedSuggestions);
          setIsOpen(true);
          setSelectedIndex(-1);
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
        return;
      } catch (error) {
        console.error('❌ CustomAutocomplete: New AutocompleteSuggestion API error:', error);
        setLoading(false);
      }
    }

    // If we get here, the new API failed or isn't available
    // Only use legacy if we're in complete fallback mode (when new library loading failed)
    if (autocompleteService.current) {
      console.log('🔄 CustomAutocomplete: Using legacy AutocompleteService as last resort');
      
      // Final check - don't search if we recently made a selection
      const timeSinceLastSelection = Date.now() - lastSelectionTimeRef.current;
      if (timeSinceLastSelection < 1000) {
        console.log('🚫 Blocking searchPlaces - recent selection:', timeSinceLastSelection + 'ms ago');
        return;
      }
      
      setLoading(true);
      
      const request = {
        input: query,
        componentRestrictions: { country: 'il' },
        types: ['address'],
        language: 'he'
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        setLoading(false);
        console.log('📍 CustomAutocomplete: Legacy fallback results:', { status, predictionsCount: predictions?.length || 0 });
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedSuggestions = predictions.map(prediction => ({
            placeId: prediction.place_id,
            description: prediction.description,
            mainText: prediction.structured_formatting.main_text,
            secondaryText: prediction.structured_formatting.secondary_text
          }));
          
          setSuggestions(formattedSuggestions);
          setIsOpen(true);
          setSelectedIndex(-1);
        } else {
          console.log('⚠️ CustomAutocomplete: No results or error:', status);
          setSuggestions([]);
          setIsOpen(false);
        }
      });
    } else {
      console.log('❌ CustomAutocomplete: No autocomplete service available');
      setLoading(false);
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const selectSuggestion = async (suggestion) => {
    console.log('✅ CustomAutocomplete: Selected suggestion:', suggestion.description);
    console.log('🔒 Setting isSelecting to true and recording timestamp');
    
    // Record selection timestamp to prevent searches for a while
    lastSelectionTimeRef.current = Date.now();
    
    // Clear any pending search timeouts immediately
    if (searchTimeoutRef.current) {
      console.log('🗑️ Clearing pending search timeout');
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    setIsSelecting(true);
    setInputValue(suggestion.description);
    setIsOpen(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    
    // Reset selecting flag after a short delay
    setTimeout(() => {
      console.log('🔓 Resetting isSelecting to false');
      setIsSelecting(false);
    }, 100);

    // Use new Place.fetchFields() API (following migration guide)
    if (window.google?.maps?.places?.Place) {
      try {
        console.log('🏠 CustomAutocomplete: Getting place details with new Place.fetchFields()');
        
        // Create Place instance using place ID
        const place = new window.google.maps.places.Place({
          id: suggestion.placeId
        });

        // Use fetchFields() instead of getDetails() - following migration guide
        await place.fetchFields({
          fields: ['formattedAddress', 'location', 'displayName']
        });

        console.log('🏠 CustomAutocomplete: New API place details:', place.formattedAddress);
        
        if (place.formattedAddress && onPlaceSelected) {
          onPlaceSelected({
            formatted_address: place.formattedAddress, // camelCase from new API
            geometry: place.location ? { location: place.location } : null,
            name: place.displayName || suggestion.description
          });
        }
        return;
      } catch (error) {
        console.error('❌ CustomAutocomplete: New Place.fetchFields() error:', error);
      }
    }

    // If we get here, the new API failed or isn't available
    // Only use legacy PlacesService if we're in complete fallback mode (when new library loading failed)
    if (placesService.current) {
      console.log('🔄 CustomAutocomplete: Using legacy PlacesService as last resort');
      
      const request = {
        placeId: suggestion.placeId,
        fields: ['formatted_address', 'geometry', 'name']
      };

      placesService.current.getDetails(request, (place, status) => {
        console.log('🏠 CustomAutocomplete: Legacy fallback place details result:', { status, place: place?.formatted_address });
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          if (onPlaceSelected) {
            onPlaceSelected({
              formatted_address: place.formatted_address,
              geometry: place.geometry,
              name: place.name
            });
          }
        } else {
          console.log('⚠️ CustomAutocomplete: Failed to get place details:', status);
        }
      });
    } else {
      console.log('❌ CustomAutocomplete: No place details service available');
    }
  };

  const handleInputChange = (e) => {
    userInteracted.current = true;
    setInputValue(e.target.value);
    if (onInputChange) {
      onInputChange(e.target.value);
    }
  };

  const handleClear = () => {
    userInteracted.current = false;
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    if (onInputChange) {
      onInputChange('');
    }
    window.setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          // Record selection timestamp
          lastSelectionTimeRef.current = Date.now();
          
          // Clear any pending search timeouts before selection
          if (searchTimeoutRef.current) {
            console.log('🗑️ Clearing pending search timeout (keyboard)');
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
          }
          selectSuggestion(suggestions[selectedIndex]);
          // Blur input to close dropdown completely
          setTimeout(() => inputRef.current?.blur(), 50);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    document.body.classList.add('keyboard-open');
    document.documentElement.classList.add('keyboard-open');
    const headerEl = document.querySelector('.main-header');
    headerEl?.classList.add('keyboard-open');
    // Don't open dropdown if we recently made a selection
    const timeSinceLastSelection = Date.now() - lastSelectionTimeRef.current;
    if (timeSinceLastSelection < 1000) {
      console.log('🚫 Skipping focus dropdown open - recent selection:', timeSinceLastSelection + 'ms ago');
      return;
    }
    
    if (suggestions.length > 0 && !isSelecting) {
      console.log('👁️ Opening dropdown on focus');
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    // Don't close immediately if we're in the process of selecting
    if (isSelecting) {
      return;
    }
    setTimeout(() => {
      if (!isSelecting) {
        setIsOpen(false);
        document.body.classList.remove('keyboard-open');
        document.documentElement.classList.remove('keyboard-open');
        const headerEl = document.querySelector('.main-header');
        headerEl?.classList.remove('keyboard-open');
      }
    }, 150);
  };

  // Update input value when prop changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!autoFocus || !inputRef.current) return;
    const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
    if (isCoarsePointer) return;

    const timer = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [autoFocus]);

  return (
    <div className="custom-autocomplete">
      <div className="input-container">
        <input
          id={id}
          name={name}
          ref={inputRef}
          type="text"
          autoComplete="street-address"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`${isTransparent ? 'autocomplete-input-transparent' : 'autocomplete-input'} ${className}`}
        />
        {inputValue && !loading && (
          <button
            type="button"
            className={`autocomplete-clear-btn ${isTransparent ? 'is-transparent' : ''}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClear}
            aria-label="נקה חיפוש"
          >
            <i className="fi fi-rr-cross-small" aria-hidden="true"></i>
          </button>
        )}
        {loading && (
          <div className="input-loader">
            <div className="spinner-small"></div>
          </div>
        )}
      </div>
      
      {isOpen && suggestions.length > 0 && (() => {
        const isMobile = window.innerWidth <= 768;
        return createPortal(
          <div 
            ref={dropdownRef} 
            className={`autocomplete-dropdown portal${isMobile ? ' mobile-portal' : ''}`}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`
            }}
          >
            <div className="suggestions-list">
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.placeId}
                  className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => selectSuggestion(suggestion)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="suggestion-main">{suggestion.mainText}</div>
                  {suggestion.secondaryText && (
                    <div className="suggestion-secondary">{suggestion.secondaryText}</div>
                  )}
                </div>
              ))}
            </div>
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

export default CustomAutocomplete;
